import React, { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { InputGroup, Form, Tab, Button, Modal } from "react-bootstrap";
import { formatValue } from "react-currency-input-field";
import { useDataFetch } from "./../../hooks/useDataFetch";
import { useSelector } from "react-redux";
import { selectAuthToken } from "./../../store/selectors/AuthSelectors";
import ApiService from "./../../services/AxiosInstance";
import PeriodicCostCreate from "../components/PeriodicCost/Create";

const fuzzyFilter = (row, columnId, value) => {
  return rankItem(row.getValue(columnId), value).passed;
};

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <InputGroup className="mb-3">
      <Form.Control
        placeholder="Buscar"
        value={globalFilter ?? ""}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      />
    </InputGroup>
  );
};

const Request = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [newCost, setNewCost] = useState(false);

  const token = useSelector(selectAuthToken);
  const [data, setData] = useState(null);
  const { getData, invalidateQuery } = useDataFetch();

  const fetchCostPeriodics = async () => {
    try {
      const response = await ApiService.get("/student/cost-periodics", {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: yearFilter },
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching cost periodics:", error);
    }
  };

  useEffect(() => {
    fetchCostPeriodics();
  }, [yearFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "year",
        header: "Año",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "identification",
        header: "Identificación",
      },
      {
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
        id: "full_name",
        header: "Nombre",
      },
      {
        accessorKey: "name",
        header: "Concepto",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "value",
        header: "Valor",
        cell: ({ getValue }) =>
          formatValue({
            value: getValue().toString(),
            groupSeparator: ".",
            decimalSeparator: ",",
            prefix: "$",
          }),
      },
      {
        accessorKey: "date_application",
        header: "Fecha de aplicación",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "state_students_cost_periodics",
        header: "Estado",
        cell: ({ getValue }) =>
          getValue() == "pending" ? "Pendiente" : "Aprobado",
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter, // Aplicar filtro fuzzy para el filtro global
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  return (
    <Tab.Container defaultActiveKey="all">
      <div className="d-flex justify-content-between">
        <select
          className="form-control mb-3"
          onChange={(e) => setYearFilter(e.target.value)}
          value={yearFilter}
          style={{ width: 100 }}
        >
          {[2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <div className="d-flex" style={{ width: 500 }}>
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          <Button
            className="btn btn-primary text-nowrap mb-3 ml-3"
            onClick={() => setNewCost(true)}
          >
            <i className="fa fa-plus scale5 mr-3" aria-hidden="true" />
            Asignar Costos
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table card-table display dataTablesCard dataTable no-footer">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
          <div className="dataTables_info">
            Mostrando{" "}
            {table.getRowModel().rows.length *
              (table.getState().pagination.pageIndex + 1)}{" "}
            de {table.getRowCount().toLocaleString()} registros
          </div>
          <div
            className="dataTables_paginate paging_simple_numbers"
            id="example2_paginate"
          >
            <Button
              className="paginate_button previous"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Anterior
            </Button>

            <Button
              className="paginate_button next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
      <Modal className="fade bd-example-modal-lg" show={newCost} size="xl">
        <Modal.Header>
          <Modal.Title>Asignar Costos Periódicos</Modal.Title>
          <Button
            variant=""
            className="close"
            onClick={() => setNewCost(false)}
          >
            <span>&times;</span>
          </Button>
        </Modal.Header>
        <Modal.Body>
          <PeriodicCostCreate
            yearFilter={yearFilter}
            refetchCost={() => {
              fetchCostPeriodics();
              setNewCost(false);
            }}
          />
        </Modal.Body>
      </Modal>
    </Tab.Container>
  );
};

export default Request;
